import { normalizeWidgetTheme, APIURL, cancelSelectEvent, changeSymbolForWidget, getCSSStyles, getSymbolDropdown, getTraderSMCardHTML, getWidgetDataURI, apiGet, toggleWidgetTheme, updateSelectedSymbolInDropdown, isHeaderPinned, getHeaderPinStateClass, getHeaderPinButtonHTML, setupHeaderPin, getNoDataHTML } from '../../helpers';
import { FinancialConfig, WidgetEvent, WidgetEventHandler, WidgetEventType } from '../../types';

export class TraderverseMarketWinLossMarketCap {
  private container: HTMLElement;
  private config: FinancialConfig;
  private eventHandlers: Map<WidgetEventType, WidgetEventHandler[]> = new Map();
  private updateInterval: number | null = null;
  private shadowRoot: ShadowRoot;
  private currentData: any = null;
  private currentChart: any;
  private widgetResizeObserver: ResizeObserver | null = null;

  constructor(config: FinancialConfig) {
    this.config = { ...this.getDefaultConfig(), ...config };
    this.config.theme = normalizeWidgetTheme(this.config.theme);
    this.container = this.resolveContainer(config.container);

    if ((this.container as any).shadowRoot) {
      this.shadowRoot = (this.container as any).shadowRoot;
    } else {
      this.shadowRoot = this.container.attachShadow({ mode: 'closed' });
    }
    this.init();

    this.widgetResizeObserver = new ResizeObserver(() => {
      this.updateDisplay();
    });
    setTimeout(() => {
      const widget = this.shadowRoot.querySelector('.widgetBox') as HTMLElement;
      if (widget) this.widgetResizeObserver?.observe(widget);
    }, 0);
  }

  private fetchSymbols(): Array<{ value: string; label: string }> {
    return [
      { label: 'AAPL', value: 'AAPL' }, { label: 'MSFT', value: 'MSFT' },
      { label: 'GOOGL', value: 'GOOGL' }, { label: 'AMZN', value: 'AMZN' },
      { label: 'NVDA', value: 'NVDA' }, { label: 'META', value: 'META' },
      { label: 'TSLA', value: 'TSLA' }, { label: 'AVGO', value: 'AVGO' },
      { label: 'PEP', value: 'PEP' }, { label: 'COST', value: 'COST' }
    ];
  }

  private getDefaultConfig(): Partial<FinancialConfig> {
    const symbols = this.fetchSymbols();
    return {
      headerPinned: true,
      symbol: symbols[0].value,
      type: "Cash and Debt",
      theme: 'default-dark',
      width: '100%',
      height: '400px',
      autosize: true,
    };
  }

  private resolveContainer(container: string | HTMLElement): HTMLElement {
    if (typeof container === 'string') {
      const el = document.getElementById(container) || document.querySelector(container);
      if (!el) throw new Error(`Container not found: ${container}`);
      return el as HTMLElement;
    }
    return container;
  }

  private async init(): Promise<void> {
    if (!this.config.symbol) this.config.symbol = this.fetchSymbols()[0].value;
    this.createWidget();
    const widget = this.shadowRoot.querySelector('.widgetBox') as HTMLElement;
    if (widget) widget.classList.add('loading');
    await this.injectChartLibrary();
    this.attachStyles();
    this.setupEventListeners();
    await this.setupChart(this.config.symbol!);
    if (widget) widget.classList.remove('loading');
    this.emit('ready', {});
    cancelSelectEvent(this.shadowRoot);
    this.config.onReady?.();
  }

  private createWidget(): void {
    let themeClass = 'defaultDarkMode';
  let dataTheme = 'default-dark';
  
  if (this.config.theme === 'white') {
    themeClass = 'whiteMode';
    dataTheme = 'white';
  } else if (this.config.theme === 'black') {
    themeClass = 'blackMode';
    dataTheme = 'black';
  } else if (this.config.theme === 'default-lite') {
  themeClass = 'defaultLightMode';
  dataTheme = 'default-lite';
}

    const legendSpans = `
      <span class="toggle-series active" data-key="Series1" style="background-color:#00FF57;"></span>
      <span class="toggle-series active" data-key="Series2" style="background-color:#7C3AED;"></span>
    `;

    const widgetHtml = `
      <div style="width: ${typeof this.config.width == "number" ? this.config.width+ 'px' : this.config.width  };height: ${typeof this.config.height == "number" ? this.config.height+ 'px' : this.config.height};" class="widgetBox ${themeClass} loading ${getHeaderPinStateClass(this.config)}" data-theme="${dataTheme}">
        <div class="headTop">
          <h4 class="title stock">${this.getTitleIconSvg()} Winners/Losers by Market Cap</h4>
          <div class="actionCol">${getHeaderPinButtonHTML(this.config)}<div class="item">
                <button class="filters-toggle" type="button"> <svg width="14" height="14" viewBox="0 0 14 14" fill="none" xmlns="http://www.w3.org/2000/svg"> <path d="M12.1794 0.950592H1.72077C1.57285 0.950469 1.42802 0.991908 1.30363 1.06995C1.17925 1.14799 1.08058 1.25932 1.01944 1.39061C0.958302 1.5219 0.937286 1.66759 0.958909 1.81023C0.980533 1.95286 1.04388 2.0864 1.14136 2.19484L5.22108 6.73834C5.34378 6.8752 5.41132 7.05084 5.41114 7.23259V10.8881C5.41114 10.9463 5.42504 11.0037 5.45175 11.0558C5.47846 11.1079 5.51725 11.1532 5.56503 11.1881L7.87343 12.8756C7.93059 12.9174 7.99856 12.9428 8.06972 12.9491C8.14088 12.9553 8.21242 12.9421 8.27633 12.911C8.34023 12.8799 8.39398 12.832 8.43154 12.7727C8.46911 12.7135 8.489 12.6452 8.489 12.5756V7.23259C8.48882 7.05084 8.55636 6.8752 8.67906 6.73834L12.7588 2.19409C13.1943 1.70959 12.8403 0.950592 12.1794 0.950592Z" stroke="currentColor" stroke-width="1.5" stroke-linecap="round"/> </svg> </button>
              </div></div>
        </div>
        <div class="inBody">
          <div class="optionsRow">
            <div class="item dropStyle01">
              
            </div>
            <span></span>
            <div class="item">
              <div class="legendsCollection">
                ${legendSpans}
              </div>
            </div>


            <div class="filtersBox sideBar hidden">
              <div class="item">
                <label>Period:
                  <select class="period-filter dropdown-btn">
                    <option value="1d">1d</option>
                    <option value="1w">1w</option>
                    <option value="1m" selected>1m</option>
                    <option value="3m">3m</option>
                    <option value="6m">6m</option>
                    <option value="1y">1y</option>
                  </select>
                </label>
              </div>
              <div class="item">
                <label>Exchange:
                  <select class="exchange-filter dropdown-btn">
                    <option value="NASDAQ" selected>NASDAQ</option>
                    <option value="NYSE">NYSE</option>
                  </select>
                </label>
              </div>
              <div class="item">
                <label>Market Cap Categories:
                  <input type="text" class="market-cap-input" value="mega_cap,large_cap,small_cap" placeholder="e.g. mega_cap,large_cap" style="width:180px;">
                </label>
              </div>
              <div class="item">
                <label>Min Threshold:
                  <input type="number" class="min-threshold-filter" value="10" min="0" step="1" style="width:60px;">
                </label>
              </div>
            </div>
          </div>
          <div class="chartDv" id="cashDebtContainer"></div>
        </div>
      </div>
    `;
    this.shadowRoot.innerHTML = widgetHtml;

    if (this.config.isSymbolSelectable && !this.config.hideSymbol) {
      const dropdown = getSymbolDropdown((data: any) => this.onSelectChange(data));
      const placeholder = this.shadowRoot.getElementById('symbol-dropdown-placeholder');
      if (placeholder && dropdown) placeholder.replaceWith(dropdown);
    }

    this.attachLegendClickHandlers();
  }

  private attachLegendClickHandlers(): void {
    const legendSpans = this.shadowRoot.querySelectorAll('.toggle-series');
    legendSpans.forEach(legend => {
      legend.addEventListener('click', () => {
        legend.classList.toggle('active');
        this.filterSeriesByLegend();
      });
    });
  }

private filterSeriesByLegend(): void {
  if (!this.currentChart || !this.currentData) return;

  const activeKeys = Array.from(
    this.shadowRoot.querySelectorAll('.toggle-series.active')
  ).map(el => (el as HTMLElement).dataset.key);

  const series = [
    {
      name: 'Gainers',
      type: 'bar',
      data: activeKeys.includes('gainers') ? this.currentData.gainers : [],
      barWidth: '20%',
      itemStyle: { color: '#00FF57' }
    },
    {
      name: 'Losers',
      type: 'bar',
      data: activeKeys.includes('losers') ? this.currentData.losers : [],
      barWidth: '20%',
      itemStyle: { color: '#FF0000' }
    },
    {
      name: 'Difference',
      type: 'bar',
      data: activeKeys.includes('difference') ? this.currentData.difference : [],
      barWidth: '20%',
      itemStyle: { color: '#3B82F6' }
    }
  ];

  this.currentChart.setOption({
    xAxis: { data: this.currentData.categories },
    series
  });
}



  onSelectChange(stock: any) {
    this.changeSymbol(stock.symbol, stock);
  }

  private async attachStyles(): Promise<void> {
    try {
      const styleEl = await getCSSStyles();
      this.shadowRoot.appendChild(styleEl);
    } catch (err) {
      console.error('Failed to load CSS file:', err);
    }
  }

  private setupEventListeners(): void {
    setupHeaderPin(this.shadowRoot, this.config);
    const symbolDropdown = this.shadowRoot.querySelector('.symbol-dropdown') as HTMLSelectElement;
    symbolDropdown?.addEventListener('change', e => {
      const value = (e.target as HTMLSelectElement).value;
      this.changeSymbol(value);
    });
    if (symbolDropdown && this.config.symbol) symbolDropdown.value = this.config.symbol;

    const filtersToggle = this.shadowRoot.querySelector('.filters-toggle') as HTMLButtonElement;
    const filtersBox = this.shadowRoot.querySelector('.filtersBox') as HTMLElement;
    if (filtersToggle && filtersBox) {
      filtersToggle.addEventListener('click', () => {
        filtersBox.classList.toggle('hidden');
      });
    }

    const periodFilter = this.shadowRoot.querySelector('.period-filter') as HTMLSelectElement;
    const exchangeFilter = this.shadowRoot.querySelector('.exchange-filter') as HTMLSelectElement;
    const minThresholdFilter = this.shadowRoot.querySelector('.min-threshold-filter') as HTMLInputElement;
    const marketCapInput = this.shadowRoot.querySelector('.market-cap-input') as HTMLInputElement;

    const updateChartWithFilters = () => {
      this.setupChart(this.config.symbol!);
    };
    periodFilter?.addEventListener('change', updateChartWithFilters);
    exchangeFilter?.addEventListener('change', updateChartWithFilters);
    minThresholdFilter?.addEventListener('input', updateChartWithFilters);
    if (marketCapInput) {
      marketCapInput.addEventListener('keydown', (e) => {
        if (e.key === 'Enter') {
          updateChartWithFilters();
        }
      });
    }
  }

  private async injectChartLibrary(): Promise<boolean> {
    return new Promise<boolean>((resolve) => {
      if ((window as any).echarts) return resolve(true);
      if (this.shadowRoot.querySelector('script[data-chart-lib]')) {
        const script = this.shadowRoot.querySelector('script[data-chart-lib]') as HTMLScriptElement;
        script.onload = () => resolve(true);
      } else {
        const script = document.createElement('script');
        script.src = 'https://cdn.jsdelivr.net/npm/echarts@5/dist/echarts.min.js';
        script.async = true;
        script.setAttribute('data-chart-lib', 'true');
        this.shadowRoot.appendChild(script);
        script.onload = () => resolve(true);
      }
    });
  }

  private async setupChart(symbol: string): Promise<void> {
    try {
      const periodFilter = this.shadowRoot.querySelector('.period-filter') as HTMLSelectElement;
      const exchangeFilter = this.shadowRoot.querySelector('.exchange-filter') as HTMLSelectElement;
      const minThresholdFilter = this.shadowRoot.querySelector('.min-threshold-filter') as HTMLInputElement;

      const marketCapInput = this.shadowRoot.querySelector('.market-cap-input') as HTMLInputElement;

      const p_period = periodFilter?.value || '1m';
      const p_exchange = exchangeFilter?.value || 'NASDAQ';
      const p_min_threshold = minThresholdFilter?.value || '10';
      let p_market_cap_categories: string[] = [];
      if (marketCapInput && marketCapInput.value.trim()) {
        p_market_cap_categories = marketCapInput.value.split(',').map(v => v.trim()).filter(Boolean);
      }

      const params = new URLSearchParams();
      params.append('p_period', p_period);
      params.append('p_exchange', p_exchange);
      if (p_market_cap_categories.length > 0) {
        params.append('p_market_cap_categories', p_market_cap_categories.join(','));
      }
      params.append('p_min_threshold', p_min_threshold);

      const url = `${APIURL}/v1/discover/winner-losers-by-market-cap?${params.toString()}`;
      const res = await apiGet({
        url,
        method: "GET",
      });

      if (!res.ok) throw new Error(`API error: ${res.status}`);
      const json = await res.json();
      const dataArr = Array.isArray(json.data) ? json.data : [];
      const filtered = dataArr.filter((d: any) => d.exchange === p_exchange);
      if (filtered.length === 0) {
        this.currentData = null;
        this.updateDisplay();
        return;
      }
      const categories = filtered.map((d: any) =>
        String(d.market_cap_category ?? '').replace(/_/g, ' ')
      );
      const gainers = filtered.map((d: any) => d.gainers);
      const losers = filtered.map((d: any) => d.losers);
      const difference = filtered.map((d: any) => d.difference);
      this.currentData = { categories, gainers, losers, difference };

      this.updateDisplay();
      this.emit('dataUpdate', this.currentData);
    } catch (err) {
      console.error("Error fetching chart data:", err);
      this.currentData = null;
      this.updateDisplay();
    }
  }

  private updateDisplay(): void {
    const el = this.shadowRoot.querySelector('#cashDebtContainer') as HTMLElement;
    if (!el) return;

    if (!(window as any).echarts) {
      setTimeout(() => this.updateDisplay(), 100);
      return;
    }

    if (!this.currentData || !this.currentData.categories?.length) {
      el.removeAttribute('data-testing-id');
      if (this.currentChart) {
        try { this.currentChart.dispose(); } catch { /* ignore */ }
        this.currentChart = null;
      }
      el.innerHTML = getNoDataHTML();
      return;
    }

    el.setAttribute('data-testing-id', 'Market Win Loss Market Cap');
    this.renderMarketCapWinLossChart();
    this.updateLegends();
  }

  private renderMarketCapWinLossChart(): void {
    const container = this.shadowRoot.querySelector('#cashDebtContainer');
    if (!container) return;
    const html = `<div class="cashDebtMain"><div id="chartContainer" class="chart"></div></div>`;
    container.innerHTML = html;
    this.drawMarketCapWinLossChart();
  }

  private drawMarketCapWinLossChart(): void {
    const echarts = (window as any).echarts;
    const container = this.shadowRoot.querySelector('#chartContainer') as HTMLDivElement;
    if (!container) return;
    const instance = echarts.getInstanceByDom(container);
    if (instance) echarts.dispose(instance);

    const { categories, gainers, losers, difference } = this.currentData;
    const widget = this.shadowRoot.querySelector('.widgetBox') as HTMLElement;
    const textColor = getComputedStyle(widget).getPropertyValue('--charts-text-color');
    const splitLineColor = getComputedStyle(widget).getPropertyValue('--charts-split-line-color');
    const fontSize = getComputedStyle(widget).getPropertyValue('--font-size-sm');

    const option = {
      backgroundColor: 'transparent',
      animation: false,
      grid: { left: 0, right: 0, top: 10, bottom: 0, containLabel: true },
      tooltip: { trigger: 'axis' },
      // legend: { data: ['Gainers', 'Losers', 'Difference'], textStyle: { color: textColor, fontSize: fontSize } },
      xAxis: { type: 'category', data: categories, axisLabel: { color: textColor, fontSize: fontSize, margin: 16 } },
      yAxis: {
        type: 'value',
        axisLabel: { color: textColor, fontSize: fontSize },
        splitLine: { show: true, lineStyle: { color: splitLineColor, type: 'dashed' } }
      },
      series: [
        { name: 'Gainers', type: 'bar', data: gainers, barWidth: '20%', itemStyle: { color: '#00FF57' } },
        { name: 'Losers', type: 'bar', data: losers, barWidth: '20%', itemStyle: { color: '#FF0000' } },
        { name: 'Difference', type: 'bar', data: difference, barWidth: '20%', itemStyle: { color: '#3B82F6' } }
      ]
    };

    const chart = echarts.init(container, null, { backgroundColor: getComputedStyle(widget).getPropertyValue('--bg-primary').trim() });
    chart.setOption(option);
    this.currentChart = chart;
  }


  private updateLegends(): void {
  const legendContainer = this.shadowRoot.querySelector('.legendsCollection');
  if (!legendContainer || !this.currentData) return;

  const legends = [
    { key: 'gainers', color: '#00FF57' },
    { key: 'losers', color: '#FF0000' },
    { key: 'difference', color: '#3B82F6' }
  ];

  legendContainer.innerHTML = legends
    .map(
      l =>
        `<span class="toggle-series active" data-key="${l.key}" style="background-color:${l.color};"></span>`
    )
    .join('');

  this.attachLegendClickHandlers();
}


  private changeSymbol(stockId: string, stock: any = null): void {
    changeSymbolForWidget(this.shadowRoot, this.config, stock, stockId, (stockId: any) => this.setupChart(stockId), (type: any, data: any) => this.emit(type, data));
  }

  public on(type: WidgetEventType, handler: WidgetEventHandler): void {
    if (!this.eventHandlers.has(type)) this.eventHandlers.set(type, []);
    this.eventHandlers.get(type)!.push(handler);
  }
  public off(type: WidgetEventType, handler: WidgetEventHandler): void {
    const list = this.eventHandlers.get(type);
    if (list) this.eventHandlers.set(type, list.filter(h => h !== handler));
  }
  public setSymbol(symbol: string): void {
    this.changeSymbol(symbol);
    const dropdown = this.shadowRoot.querySelector('.symbol-dropdown') as HTMLSelectElement;
    if (dropdown) dropdown.value = symbol;
  }
  public async getWidgetImage(): Promise<string> {
    const widget = this.shadowRoot.querySelector('.widgetBox') as HTMLElement;
    return await getWidgetDataURI(widget);
  }
  
  public setTheme(theme: 'white' | 'black' | 'default-dark' | 'default-lite' | 'default-lite'): void {
    if (!this.shadowRoot) return;
  
    this.config.theme = toggleWidgetTheme(
      this.shadowRoot,
      theme
    ) as 'white' | 'black' | 'default-dark' | 'default-lite' | 'default-lite';
  
    this.updateDisplay();
  }
  public destroy(): void {
    if (this.updateInterval) { clearInterval(this.updateInterval); this.updateInterval = null; }
    if (this.widgetResizeObserver) { this.widgetResizeObserver.disconnect(); this.widgetResizeObserver = null; }
    this.eventHandlers.clear(); this.shadowRoot.innerHTML = '';
  }
  private emit(type: WidgetEventType, data: any): void {
    const handlers = this.eventHandlers.get(type);
    if (handlers) { const evt: WidgetEvent = { type, data }; handlers.forEach(h => h(evt)); }
  }
  private getTitleIconSvg(): string {
    return `<svg width="30" height="30" viewBox="0 0 30 30" fill="none" xmlns="http://www.w3.org/2000/svg"> <path d="M8.59998 23.625C8.08748 23.625 7.66248 23.2 7.66248 22.6875V20.1C7.66248 19.5875 8.08748 19.1625 8.59998 19.1625C9.11248 19.1625 9.53748 19.5875 9.53748 20.1V22.6875C9.53748 23.2125 9.11248 23.625 8.59998 23.625Z" fill="currentColor"/> <path d="M15 23.625C14.4875 23.625 14.0625 23.2 14.0625 22.6875V17.5C14.0625 16.9875 14.4875 16.5625 15 16.5625C15.5125 16.5625 15.9375 16.9875 15.9375 17.5V22.6875C15.9375 23.2125 15.5125 23.625 15 23.625Z" fill="currentColor"/> <path d="M21.4 23.625C20.8875 23.625 20.4625 23.2 20.4625 22.6875V14.9125C20.4625 14.4 20.8875 13.975 21.4 13.975C21.9125 13.975 22.3375 14.4 22.3375 14.9125V22.6875C22.3375 23.2125 21.925 23.625 21.4 23.625Z" fill="currentColor"/> <path d="M8.59997 16.475C8.17497 16.475 7.79997 16.1875 7.68747 15.7625C7.56247 15.2625 7.86247 14.75 8.37497 14.625C12.975 13.475 17.025 10.9625 20.1125 7.37502L20.6875 6.70002C21.025 6.31252 21.6125 6.26252 22.0125 6.60002C22.4 6.93752 22.45 7.52502 22.1125 7.92502L21.5375 8.60002C18.2 12.5 13.8 15.2125 8.82497 16.45C8.74997 16.475 8.67497 16.475 8.59997 16.475Z" fill="currentColor"/> <path d="M21.4001 11.9C20.8876 11.9 20.4626 11.475 20.4626 10.9625V8.25H17.7375C17.225 8.25 16.8 7.825 16.8 7.3125C16.8 6.8 17.225 6.375 17.7375 6.375H21.4001C21.9126 6.375 22.3376 6.8 22.3376 7.3125V10.975C22.3376 11.4875 21.9251 11.9 21.4001 11.9Z" fill="currentColor"/> <path d="M18.75 28.4375H11.25C4.4625 28.4375 1.5625 25.5375 1.5625 18.75V11.25C1.5625 4.4625 4.4625 1.5625 11.25 1.5625H18.75C25.5375 1.5625 28.4375 4.4625 28.4375 11.25V18.75C28.4375 25.5375 25.5375 28.4375 18.75 28.4375ZM11.25 3.4375C5.4875 3.4375 3.4375 5.4875 3.4375 11.25V18.75C3.4375 24.5125 5.4875 26.5625 11.25 26.5625H18.75C24.5125 26.5625 26.5625 24.5125 26.5625 18.75V11.25C26.5625 5.4875 24.5125 3.4375 18.75 3.4375H11.25Z" fill="currentColor"/> </svg>`;
  }

}

declare global { interface Window { TraderverseMarketWinLossMarketCap: typeof TraderverseMarketWinLossMarketCap; } }
if (typeof window !== 'undefined') { window.TraderverseMarketWinLossMarketCap = TraderverseMarketWinLossMarketCap; }